import { html } from 'lit';
import { isSame } from '../../utility/strings/strings-utils.js';
import { registerMemberWorkspaceModule } from '../../pages/app-members/workspace/member-workspace-modules.js';
import { registerSiteWorkspaceModule } from '../../pages/app-members/workspace/site-workspace-modules.js';
import { terms } from '../../language/terms.js';
import {
  canOpenCertificateRegister,
  certificateCreateHref,
  certificateHref,
  certificateRegisterHref,
  navigateTo,
  siteCertificatesHref,
} from '../../modules/glass-accreditation/glass-navigation.js';
import {
  GLASS_CERTIFICATES_TAB_ID,
  GLASS_CERTIFICATES_TAB_LABEL,
} from '../../modules/glass-accreditation/glass-workspace-tabs.js';
import type {
  CertificateCreateDetail,
  CertificateOpenDetail,
  SiteOpenDetail,
} from '../../modules/glass-accreditation/components/glass-company-certificates.js';

/**
 * The Prime Glass Manufacturer domain's workspace tabs (imported by domains/index.ts, which
 * registers every domain's tabs at once).
 *
 * The catalogue code is `Prime.Glass.Manufacturer`. A company never holds it itself -
 * operation types are site-scoped - so the company tab is gated by INHERITANCE from the
 * company's sites, while the site tabs are gated by the site's own grant.
 */
const PRIME_GLASS_MANUFACTURER = 'Prime.Glass.Manufacturer';

/** The company's glass certificates, hosted on the company workspace. */
registerMemberWorkspaceModule({
  id: GLASS_CERTIFICATES_TAB_ID,
  label: GLASS_CERTIFICATES_TAB_LABEL,
  icon: 'award',
  order: 50,
  inheritedDomains: [PRIME_GLASS_MANUFACTURER],
  async load() {
    await import('../../modules/glass-accreditation/components/glass-company-certificates.js');
  },
  render: (context) => {
    const companyId = context.group?.id ?? '';
    return html`
      <glass-company-certificates
        company-id=${companyId}
        link-sites
        ?link-register=${canOpenCertificateRegister()}
        @certificate-open=${(e: CustomEvent<CertificateOpenDetail>) =>
          navigateTo(
            certificateHref(
              e.detail.certificate.certificateType,
              e.detail.certificate.id
            )
          )}
        @certificate-create=${(e: CustomEvent<CertificateCreateDetail>) =>
          navigateTo(certificateCreateHref(e.detail.certificateType, e.detail))}
        @site-open=${(e: CustomEvent<SiteOpenDetail>) =>
          navigateTo(
            siteCertificatesHref(companyId, e.detail.site.loginSiteId)
          )}
        @register-open=${() => navigateTo(certificateRegisterHref())}
      ></glass-company-certificates>
    `;
  },
});

/**
 * One site's glass certificates, hosted on the site workspace beside the settings tab
 * (architect, 2026-09-06: "a new tab added on either company or site, not inside the glass
 * processor config tab"). The domain grant's instance id is the GlassProcessorSite the
 * certificates hang off, which is what the job scopes by.
 */
registerSiteWorkspaceModule({
  id: GLASS_CERTIFICATES_TAB_ID,
  label: GLASS_CERTIFICATES_TAB_LABEL,
  icon: 'award',
  order: 49, // Before the domain's own configuration tab (50)
  domains: [PRIME_GLASS_MANUFACTURER],
  async load() {
    await import('../../modules/glass-accreditation/components/glass-company-certificates.js');
  },
  render: (context) => {
    const grant = context.domains.find(
      (d) => d.active && isSame(d.domainCode ?? '', PRIME_GLASS_MANUFACTURER)
    );
    const glassSiteId = grant?.domainInstanceId ?? '';
    return html`
      <glass-company-certificates
        company-id=${context.groupId}
        site-id=${glassSiteId}
        ?link-register=${canOpenCertificateRegister()}
        @certificate-open=${(e: CustomEvent<CertificateOpenDetail>) =>
          navigateTo(
            certificateHref(
              e.detail.certificate.certificateType,
              e.detail.certificate.id
            )
          )}
        @certificate-create=${(e: CustomEvent<CertificateCreateDetail>) =>
          navigateTo(certificateCreateHref(e.detail.certificateType, e.detail))}
        @register-open=${() => navigateTo(certificateRegisterHref())}
      ></glass-company-certificates>
    `;
  },
});

/** The site's certificate expiry notification settings: the domain's configuration tab. */
registerSiteWorkspaceModule({
  id: 'domain-glass-processor-site',
  label: terms.primeGlassManufacturer.value,
  icon: 'building',
  order: 50, // Before glass-dataentry (51) and glass-auditor (52)
  section: 'Configuration',
  domains: [PRIME_GLASS_MANUFACTURER],
  async load() {
    // Lazy-load the site configuration component
    await import('./glass-processor-site-config.js');
  },
  render: (context) => {
    return html`
      <glass-processor-site-config
        .site=${context.site}
        .domains=${context.domains}
      ></glass-processor-site-config>
    `;
  },
});
